import { useEffect } from 'react';
import { createRootRoute, Outlet, useRouterState } from '@tanstack/react-router';
// import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';
import { useQuery } from '@tanstack/react-query';
import { AppShell } from '@components/layout';
import GlobalLoader from '@components/ui/GlobalLoader/GlobalLoader';
import { getSession } from '@/services/authService';
import { getStringBundle } from '@/services/localizationService';
import { getEffectiveNavigation } from '@/services/navigationService';
import { useAuthStore } from '@/stores/authStore';
import { useLocalizationStore } from '@/stores/localizationStore';
import { useAuthBroadcast, broadcastLoggedIn } from '@/hooks/useAuthBroadcast';
import { AppDsProvider } from '@lib/dsProvider';

// Ensure staticData type augmentation is loaded
import '@lib/routerTypes';

export const Route = createRootRoute({
  component: RootLayout,
});

function RootLayout() {
  const matches = useRouterState({ select: (s) => s.matches });
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const showShell = !matches.some(
    (m) => (m.staticData as Record<string, unknown> | undefined)?.hideShell,
  );

  const setSession = useAuthStore((s) => s.setSession);
  const session = useAuthStore((s) => s.session);
  const userType = useAuthStore((s) => s.userType);
  const language = useLocalizationStore((s) => s.language);
  const setLanguage = useLocalizationStore((s) => s.setLanguage);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['session'],
    queryFn: getSession,
    retry: false,
  });

  // Listen for logout/session-expired/logged-in from other tabs
  useAuthBroadcast();

  useEffect(() => {
    if (data && !session) {
      setSession(data);
      // Notify other tabs that a session is now active
      broadcastLoggedIn();

      // Initialize locale from session if localStorage has no preference
      const storedLang = localStorage.getItem('wp_language');
      if (!storedLang && data.user.locale) {
        setLanguage(data.user.locale);
      }
    }
  }, [data, session, setSession, setLanguage]);

  const organizationId = session?.organization.id ?? undefined;
  const isEmployee = userType === 'EMPLOYEE' || pathname.startsWith('/e');

  // Localization string bundle. Gated on the session; shares its query key with
  // useTranslation, so it dedupes to a single fetch. The boot loader waits on
  // this so the UI never flashes English-fallback strings before the localized
  // bundle is applied.
  const localizationQuery = useQuery({
    queryKey: ['localization', 'bundle', language, organizationId ?? ''],
    queryFn: () => getStringBundle(language, organizationId),
    staleTime: Infinity,
    gcTime: 30 * 60 * 1000,
    enabled: !!session,
  });

  // Effective navigation (sidebar config). Shares its query key + enabled
  // clause with AppShell, so it dedupes to a single fetch. The boot loader
  // waits on this (when the shell is shown) so the sidebar never flashes from
  // the static fallback to the API-driven items.
  const navigationQuery = useQuery({
    queryKey: ['effectiveNavigation'],
    queryFn: () => getEffectiveNavigation(),
    enabled: !!session && !isEmployee,
  });

  // Session fetch failed — the envelope interceptor's handleSessionExpiry()
  // has already redirected to /login. Render nothing while the browser navigates.
  if (isError) {
    return null;
  }

  // Boot gate — hold the branded loader until the session, the localization
  // bundle, and (when the shell is shown) the navigation config have all
  // settled and been applied. Without this, first paint flickers: English
  // fallbacks → localized strings, and a static → API-driven sidebar.
  const sessionReady = !!session;
  const localizationReady = localizationQuery.isFetched;
  const navigationReady = !showShell || isEmployee || navigationQuery.isFetched;

  if (isLoading || !sessionReady || !localizationReady || !navigationReady) {
    return <GlobalLoader />;
  }

  if (!showShell) {
    return (
      <AppDsProvider>
        <Outlet />
        {/* {import.meta.env.DEV && location.hostname === 'localhost' && <TanStackRouterDevtools position="bottom-right" />} */}
      </AppDsProvider>
    );
  }

  return (
    <AppDsProvider>
      <AppShell>
        <Outlet />
      </AppShell>
      {/* {import.meta.env.DEV && location.hostname === 'localhost' && <TanStackRouterDevtools position="bottom-right" />} */}
    </AppDsProvider>
  );
}
